import { faCommentDots } from '@fortawesome/free-solid-svg-icons'
import { parseISO, format } from 'date-fns'
import { useState } from 'react'
import type React from 'react'
import styled from 'styled-components'

import Button from '@/components/Button'
import UploadPanel from '@/components/UploadPanel'
import type { FetchSingleApplication } from '@/hooks/useSubject'
import type { SectionName } from '@/lib/apollo/query/get-document-requests'
import type { Application } from '@/types/Activity'
import type { AuthorType, Document, DocumentAuthor } from '@/types/Activity'
import { Colours } from '@/utils/styles'
import { isRefCompleted } from '@/utils/subjectHelpers'
import { useUserTracking } from '@/utils/tracking'

import ActionButton from './ActionButton'
import DocumentRequestSidebar from './DocumentRequests/Sidebar'
import ExpandableCard from './ExpandableCard'
import TooltipIcon from './IconGroup/TooltipIcon'
import Lightbox from './Lightbox'

export interface Props {
  application: Application
  fetchSingleApplication: FetchSingleApplication
}

const Doc = styled.img<{ $borderColour: string }>`
  max-width: 100%;
  border-radius: 5px;
  border: 3px solid ${props => props.$borderColour};
  position: relative;

  &:hover {
    cursor: pointer;
  }
`

const DocumentList = styled.div`
  margin-top: 20px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  grid-gap: 15px;
`

const AddButton = styled(Button)`
  padding: 11px 0;
  max-height: 45px;
  margin: 0px 10px;
  font-size: 0.8rem;
  max-width: 80px;
  border: none;
  background: none;

  img {
    max-width: 24px;
  }

  input[file] {
    opacity: 0;
  }
`

const Body = styled.div`
  display: grid;
  grid-template-columns: 1fr;

  transition: height 0.25s ease-in;
`

const AgentColour = Colours.orange

const SystemColour = Colours.darkGrey

const ApplicantColour = Colours.teal

const ReferencingColour = Colours.red

const DocumentUploadedByKey = styled.div`
  margin-top: 10px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(50px, 130px));
  list-style: none;
  grid-gap: 10px;
  padding: 0 20px;

  li {
    position: relative;
  }

  li::before {
    content: '■';
    position: absolute;
    left: -0.8em;
    font-size: 2em;
    top: -0.8rem;
  }

  #applicant::before {
    color: ${ApplicantColour};
  }
  #agent::before {
    color: ${AgentColour};
  }
  #referencing::before {
    color: ${ReferencingColour};
  }
  #system::before {
    color: ${SystemColour};
  }
`

const DocumentCard = styled.div<{ $borderColour: string }>`
  position: relative;
  overflow: hidden;

  .document-meta {
    text-transform: capitalize;
    overflow-wrap: anywhere;
  }
  #triangle {
    width: 40px;
    height: 40px;
    top: -20px;
    left: -20px;
    background: ${props => props.$borderColour};
    border-style: 0px solid;
    right: -15px;
    bottom: -4px;
    position: absolute;
    transform: rotate(-45deg);
    z-index: 1000;
  }
`

const DocumentCentre: React.FC<Props> = ({ application, fetchSingleApplication }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [startIndex, setStartIndex] = useState(0)
  const [isUploadSidebarOpen, setIsUploadSidebarOpen] = useState(false)
  const [isDocumentRequestsSidebarOpen, setIsDocumentRequestsSidebarOpen] =
    useState<boolean>(false)
  const [documentRequestSection, setDocumentRequestSection] = useState<
    SectionName | undefined
  >(undefined)

  const { trackUserActivity } = useUserTracking()

  const { subjectId, applicationId, customerId } = application
  const subjectName = `${application.context?.firstName} ${application.context?.lastName}`
  const entity = application?.context?.documents || []

  const filteredItems = entity.filter(
    ({ specifiedType }) =>
      specifiedType == null ||
      (specifiedType &&
        !['Reference Report', 'Individual Report', 'Landlord Report'].includes(
          specifiedType,
        )),
  )

  const filteredEntity = filteredItems.map(item => {
    const isItemAPdf = item.fileName.includes('.pdf')
    const pdfThumbnail = item.fileName.replace('.pdf', '.jpg')

    return {
      ...item,
      thumbnail: pdfThumbnail,
      isItemAPdf,
    }
  })

  function handleOpenDocument(index: number, doc: Document) {
    setStartIndex(index)
    setLightboxOpen(true)
    trackUserActivity('View document', {
      subjectId,
      customerId,
      fileName: doc.fileName,
    })
  }

  function getUploadedByColour(type?: AuthorType) {
    switch (type) {
      case 'Agent':
        return AgentColour
      case 'Applicant':
        return ApplicantColour
      case 'ReferencingTeam':
        return ReferencingColour
      case 'System':
        return SystemColour
      default:
        return ''
    }
  }

  function getAuthorLabel(author?: DocumentAuthor) {
    if (!author) return 'Applicant'

    switch (author.type) {
      case 'ReferencingTeam':
        if (author.authorName && author.authorEmail) {
          return `${author.authorName} - (${author.authorEmail})`
        }
        return 'Referencing Team'
      default:
        return author.type
    }
  }

  function mapDocumentType(documentType?: string): string {
    // eslint-disable-next-line eqeqeq
    if (documentType == undefined) {
      return ''
    }

    switch (documentType.toLowerCase()) {
      case 'additionaldocument':
        return 'Additional Document'
    }
    return documentType
  }

  const getDocumentPreview = (document: Document) => {
    if (document.watermarkedThumbnail) {
      return document.watermarkedThumbnail
    }
    return document.thumbnail
  }

  return (
    <>
      {lightboxOpen && (
        <Lightbox
          images={filteredEntity}
          onClose={() => setLightboxOpen(false)}
          startIndex={startIndex}
          customerId={application.customerId}
          subjectId={application.subjectId}
        />
      )}
      {isUploadSidebarOpen && (
        <UploadPanel
          application={application}
          onClose={() => setIsUploadSidebarOpen(false)}
          fetchSingleApplication={fetchSingleApplication}
        />
      )}
      {isDocumentRequestsSidebarOpen && documentRequestSection !== undefined && (
        <DocumentRequestSidebar
          customerId={customerId}
          applicationId={applicationId}
          subjectId={subjectId}
          onClose={() => {
            setIsDocumentRequestsSidebarOpen(false)
            setDocumentRequestSection(undefined)
          }}
          sectionName={documentRequestSection}
          subjectName={subjectName}
        />
      )}
      <ExpandableCard
        isExpanded={true}
        label="Document Centre"
        icon="document"
        expandedContent={
          <Body>
            <div className="side-by-side-flex-end">
              <AddButton
                onClick={() => setIsUploadSidebarOpen(true)}
                aria-label="Add document"
              >
                <img
                  src="/assets/svg/add.svg"
                  alt="line illustration of a pen drawing a line"
                />
              </AddButton>
            </div>

            <strong>Uploaded by</strong>
            <DocumentUploadedByKey>
              <li id="applicant">Applicant</li>
              <li id="agent">Agent</li>
              <li id="referencing">Referencing</li>
              <li id="system">System</li>
            </DocumentUploadedByKey>

            <DocumentList>
              {filteredEntity.map((doc, index) => {
                const createdAtDate =
                  doc.createdAt !== undefined ? parseISO(doc.createdAt) : undefined

                const formattedCreatedAtDate = createdAtDate
                  ? format(createdAtDate, 'dd/MM/yyyy')
                  : undefined

                return (
                  <TooltipIcon
                    id={`document-${index + 1}`}
                    key={index}
                    tooltipMessage={`File Uploaded ${formattedCreatedAtDate} by ${getAuthorLabel(
                      doc.uploadedBy,
                    )}
                  `}
                    icon={
                      <DocumentCard
                        $borderColour={getUploadedByColour(doc.uploadedBy?.type)}
                      >
                        <div id="triangle"></div>
                        <Doc
                          onClick={() => handleOpenDocument(index, doc)}
                          src={getDocumentPreview(doc)}
                          $borderColour={getUploadedByColour(doc.uploadedBy?.type)}
                        />
                        <div className="side-by-side-flex-start" style={{ marginTop: 5 }}>
                          <img
                            src="/assets/svg/document.svg"
                            alt=""
                            style={{ marginRight: 5 }}
                          />
                          <strong className="document-meta">
                            {formattedCreatedAtDate}
                          </strong>
                        </div>
                        <div className="side-by-side-flex-start" style={{ marginTop: 5 }}>
                          {doc.uploadedBy?.authorName && (
                            <>
                              <img
                                src="/assets/svg/user.svg"
                                alt=""
                                width={16}
                                style={{ marginRight: 5 }}
                              />
                              <strong className="document-meta">
                                {doc.uploadedBy.authorName}
                              </strong>
                            </>
                          )}
                        </div>
                        <div className="side-by-side-flex-start" style={{ marginTop: 5 }}>
                          <>
                            <img
                              src="/assets/svg/info.svg"
                              alt=""
                              width={16}
                              style={{ marginRight: 5 }}
                            />
                            <strong className="document-meta">
                              {mapDocumentType(doc.specifiedType)}
                            </strong>
                          </>
                        </div>
                        {doc?.requestDetails?.additionalContext !== undefined &&
                          doc?.requestDetails?.additionalContext !== '' && (
                            <div style={{ marginTop: 5 }}>
                              <ActionButton
                                disabled={!isRefCompleted(application)}
                                label="Comments"
                                icon={faCommentDots}
                                onClick={() => {
                                  setDocumentRequestSection(
                                    doc.requestDetails?.documentSection,
                                  )
                                  setIsDocumentRequestsSidebarOpen(true)
                                }}
                              />
                            </div>
                          )}
                      </DocumentCard>
                    }
                  />
                )
              })}
            </DocumentList>
          </Body>
        }
      />
    </>
  )
}

export default DocumentCentre
